"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FunctionKeyVocabularyResponse = exports.FunctionKeyVocabularyEntry = exports.FunctionKeyCategory = exports.CatalogHotkeyReorderRequest = exports.CatalogHotkeyNodePatch = exports.CatalogHotkeyNodeCreate = exports.CatalogHotkeyTreeAdmin = exports.CatalogHotkeyNodeAdmin = exports.CatalogHotkeysBootstrap = exports.CatalogHotkeyTree = exports.CatalogHotkeyNode = exports.CatalogHotkeyTreeVersion = exports.CATALOG_HOTKEY_SERVED_TREE_NODE_MAX = exports.CATALOG_HOTKEY_SOURCED_NODE_CAP = exports.CATALOG_HOTKEY_TREE_MAX_DEPTH = exports.CATALOG_HOTKEY_TREE_NODE_CAP = exports.SPRINT_4_7_FUNCTION_KEYS = exports.FunctionKey = exports.CatalogItemIdRef = exports.CATALOG_ITEM_ID_REF_MESSAGE = exports.CatalogHotkeyNodeId = exports.CatalogHotkeyNodeKind = void 0;
const zod_1 = require("zod");
/**
 * Catalog Hotkeys contracts (Sprint 4.7).
 *
 * The cashier-facing catalogue panel on the left 45% of the POS
 * screen. Terminals consume the tree on activation
 * (`CatalogBootstrap.hotkeys`) and on in-session polls
 * (`GET /sync/catalog/hotkeys?since=<version>`). Authoring lives on
 * the Backoffice side under `/api/v1/catalog-hotkeys/`.
 *
 * Origin: `reform-lite-terminals` commit `bd35b3f` shipped a local
 * `catalog_buttons` Drift table with demo seed. This contract is
 * the canonical wire shape both sides agree on; the terminals' Drift
 * schema mirrors it 1:1.
 *
 * See `apps/bos/docs/modules/catalog-hotkeys.md` for the full spec.
 */
// ---------- Primitive shapes ----------
exports.CatalogHotkeyNodeKind = zod_1.z.enum(['group', 'item', 'function']);
/**
 * Node id — cuid-shaped on the server side, but kept as a generic
 * length-bounded string here so future id schemes (e.g. ULID) don't
 * require a contract bump.
 */
exports.CatalogHotkeyNodeId = zod_1.z.string().min(1).max(120);
/**
 * Catalog item id, used to validate the payload of `kind: 'item'` nodes
 * at the contract layer. The strict server-side FK check still runs on
 * every write — this is the cheap wire-shape gate.
 *
 * RL-790: a cuid (Prisma's `@default(cuid())`) OR a canonical UUID. The
 * Sanitex catalogue provisioner inserts items with `randomUUID()`, and a
 * cuid-only gate refused every item button on such a tenant.
 */
exports.CATALOG_ITEM_ID_REF_MESSAGE = 'catalog item id must be a cuid or a UUID';
exports.CatalogItemIdRef = zod_1.z.string().regex(
// cuid stays lowercase-only; the UUID branch is case-insensitive.
/^(?:c[a-z0-9]{20,}|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/, exports.CATALOG_ITEM_ID_REF_MESSAGE);
/**
 * Function-key shape — lowercase kebab token, optionally followed by
 * `:argument`. Vocabulary validity (whether a shape-valid key exists
 * in the current server vocabulary) is enforced at the service
 * boundary, not here.
 *
 * The initial Sprint 4.7 vocabulary contained `discount:10`,
 * `discount:20`, and `discount:30`. The runtime vocabulary also
 * includes operational functions and is fetched from
 * `GET /catalog-hotkeys/function-keys` rather than hardcoded here.
 */
exports.FunctionKey = zod_1.z
    .string()
    .regex(/^[a-z][a-z0-9_]*(?::[a-z0-9_-]+)?$/, 'lowercase kebab function key, optional :argument');
exports.SPRINT_4_7_FUNCTION_KEYS = ['discount:10', 'discount:20', 'discount:30'];
// ---------- Tree limits (single source of truth — server + UI consume) ----------
exports.CATALOG_HOTKEY_TREE_NODE_CAP = 500;
exports.CATALOG_HOTKEY_TREE_MAX_DEPTH = 3;
/**
 * MICRO-29: ceiling on the nodes an integration projects (GP
 * `HierarchyCatalogsUpdate`). `CATALOG_HOTKEY_TREE_NODE_CAP` bounds what an
 * operator authors in the editor and does not count these.
 */
exports.CATALOG_HOTKEY_SOURCED_NODE_CAP = 5_000;
/**
 * MICRO-29: the most nodes a served tree (sync, bootstrap, admin read) can
 * carry — the authored cap plus the sourced one. A tree with no sourced nodes
 * stays within `CATALOG_HOTKEY_TREE_NODE_CAP`, as before.
 */
exports.CATALOG_HOTKEY_SERVED_TREE_NODE_MAX = exports.CATALOG_HOTKEY_TREE_NODE_CAP + exports.CATALOG_HOTKEY_SOURCED_NODE_CAP;
exports.CatalogHotkeyTreeVersion = zod_1.z.number().int().min(1);
/**
 * Enforces the kind ↔ payload invariants documented in the module
 * spec. Used as a `superRefine` callback on every node-shaped schema
 * so the rules can't drift between read and write paths.
 */
function refineKindPayload(node, ctx) {
    if (node.kind === 'group' && node.payload !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['payload'],
            message: 'group nodes must have a null payload',
        });
        return;
    }
    if (node.kind === 'item') {
        if (node.payload === null) {
            ctx.addIssue({
                code: 'custom',
                path: ['payload'],
                message: 'item nodes require a catalog item id payload',
            });
        }
        else if (!exports.CatalogItemIdRef.safeParse(node.payload).success) {
            ctx.addIssue({
                code: 'custom',
                path: ['payload'],
                message: 'item payload must be a catalog item id: a cuid or a UUID',
            });
        }
        return;
    }
    if (node.kind === 'function') {
        if (node.payload === null) {
            ctx.addIssue({
                code: 'custom',
                path: ['payload'],
                message: 'function nodes require a function-key payload',
            });
        }
        else if (!exports.FunctionKey.safeParse(node.payload).success) {
            ctx.addIssue({
                code: 'custom',
                path: ['payload'],
                message: 'function payload must be a lowercase kebab function key',
            });
        }
    }
}
// ---------- Read shape (returned by sync surfaces) ----------
/**
 * Bare-bones node fields used by both the public read shape and the
 * admin shape. Kept as a plain `z.object` (no refinement) so it can
 * be extended via `.extend()` for the admin variant.
 */
const CatalogHotkeyNodeBase = zod_1.z.object({
    id: exports.CatalogHotkeyNodeId,
    parentId: exports.CatalogHotkeyNodeId.nullable(),
    sortOrder: zod_1.z.number().int().min(0).max(10_000),
    label: zod_1.z.string().min(1).max(120),
    kind: exports.CatalogHotkeyNodeKind,
    payload: zod_1.z.string().min(1).max(255).nullable(),
    iconName: zod_1.z.string().min(1).max(64).nullable(),
    /**
     * VL-283: the catalog category a `group` node opens. Null for an unbound
     * group and for every non-group kind.
     *
     * RFL-206 added this to the ADMIN shape only, so the terminal never saw
     * which category a hotkey opened and resolved it by matching the button's
     * own label against the category names. That works exactly as long as the
     * two read the same: VL-278 is a node named «Напої» bound to «Безалкогольні
     * напої», which matches nothing, and the screen opens on the first category
     * in the rail instead. Bohdan's decision on VL-271 was that a `group`
     * hotkey must carry the category reference itself.
     *
     * On the base rather than the admin extension, because both shapes need it
     * now and one definition cannot drift from the other.
     */
    categoryId: exports.CatalogHotkeyNodeId.nullable().optional(),
    /**
     * MICRO-29: the label in other languages, keyed by lowercase ISO-639 code
     * (`{ "lt": "Gėrimai", "en": "Drinks" }`). `label` stays the tenant-language
     * text, so a terminal that ignores this field renders exactly what it did.
     *
     * Emitted only for nodes that carry translations (today: nodes projected from
     * GP `HierarchyCatalogsUpdate`); every other node is serialised without the
     * key, byte-for-byte as before.
     */
    labelTranslations: zod_1.z
        .record(zod_1.z.string().regex(/^[a-z]{2,3}$/), zod_1.z.string().min(1).max(120))
        .optional(),
});
/**
 * Terminal-facing node shape — what the sync endpoints emit on the
 * wire. No optimistic-concurrency or audit columns; terminals never
 * mutate the tree, so they don't need them. The cross-field
 * invariants are enforced via `superRefine`.
 *
 * VL-283: this now carries `categoryId`. Terminals tolerate its absence —
 * every lane in the field today receives nodes without it and falls back to
 * label matching — so shipping it is additive and needs no terminal release
 * to be safe.
 */
exports.CatalogHotkeyNode = CatalogHotkeyNodeBase.superRefine(refineKindPayload);
/**
 * Full tree response — `GET /catalog-hotkeys/` (admin) and
 * `GET /sync/catalog/hotkeys` (terminal). `nodes` is a flat list;
 * terminals reconstruct parent/child via `parentId`.
 */
exports.CatalogHotkeyTree = zod_1.z.object({
    version: exports.CatalogHotkeyTreeVersion,
    nodes: zod_1.z.array(exports.CatalogHotkeyNode).max(exports.CATALOG_HOTKEY_SERVED_TREE_NODE_MAX),
    snapshotAt: zod_1.z.string().datetime(),
});
/**
 * Bootstrap-piggyback shape — `CatalogBootstrap.hotkeys` carries this.
 * No `snapshotAt` here; the bootstrap envelope already has one for the
 * whole catalog snapshot.
 */
exports.CatalogHotkeysBootstrap = zod_1.z.object({
    version: exports.CatalogHotkeyTreeVersion,
    nodes: zod_1.z.array(exports.CatalogHotkeyNode).max(exports.CATALOG_HOTKEY_SERVED_TREE_NODE_MAX),
});
// ---------- Admin read shape (returned by `GET /catalog-hotkeys/`) ----------
/**
 * H-120 — admin variant of `CatalogHotkeyNode`. Adds the
 * optimistic-concurrency token (`version`) + audit timestamps that
 * the authoring UI needs for stale-write detection and operator
 * visibility. Sync surfaces do NOT emit these fields (they're admin
 * concerns, not terminal concerns), so we keep the two shapes
 * separate to make the split explicit at the type level.
 */
exports.CatalogHotkeyNodeAdmin = CatalogHotkeyNodeBase.extend({
    // Admin reads are current-state responses, so unlike the additive terminal
    // wire shape they must always state whether the group is bound.
    categoryId: exports.CatalogHotkeyNodeId.nullable(),
    version: zod_1.z.number().int().min(1),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
}).superRefine(refineKindPayload);
/**
 * RFL-206 cross-field rule: a catalog category may only be bound to a
 * `group` node. Fires only when `kind` is known and non-group with a
 * non-null `categoryId` (a partial PATCH that omits `kind` is validated
 * against the merged row server-side instead).
 */
function refineCategoryBinding(node, ctx) {
    if (node.categoryId !== null &&
        node.categoryId !== undefined &&
        node.kind !== undefined &&
        node.kind !== 'group') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['categoryId'],
            message: 'a category can only be bound to a group node',
        });
    }
}
/**
 * Admin GET tree response — full snapshot with optimistic-concurrency
 * tokens on every node. Terminals use `CatalogHotkeyTree` (which
 * lacks the admin columns) instead.
 */
exports.CatalogHotkeyTreeAdmin = zod_1.z.object({
    version: exports.CatalogHotkeyTreeVersion,
    nodes: zod_1.z.array(exports.CatalogHotkeyNodeAdmin).max(exports.CATALOG_HOTKEY_SERVED_TREE_NODE_MAX),
    snapshotAt: zod_1.z.string().datetime(),
});
// ---------- Write DTOs ----------
exports.CatalogHotkeyNodeCreate = zod_1.z
    .object({
    parentId: exports.CatalogHotkeyNodeId.nullable(),
    sortOrder: zod_1.z.number().int().min(0).max(10_000),
    label: zod_1.z.string().min(1).max(120),
    kind: exports.CatalogHotkeyNodeKind,
    payload: zod_1.z.string().min(1).max(255).nullable(),
    iconName: zod_1.z.string().min(1).max(64).nullable(),
    // RFL-206: optional category binding (group nodes only).
    categoryId: zod_1.z.string().min(1).nullable().optional(),
})
    .superRefine((node, ctx) => {
    refineKindPayload(node, ctx);
    refineCategoryBinding(node, ctx);
});
/**
 * PATCH /catalog-hotkeys/nodes/:id body. All node fields optional;
 * `expectedVersion` mandatory for optimistic concurrency. Cross-field
 * check fires only if both `kind` and `payload` are present (partial
 * updates that touch only one trigger no validation — the service
 * loads the current row and applies the invariant against the merged
 * shape).
 */
exports.CatalogHotkeyNodePatch = zod_1.z
    .object({
    parentId: exports.CatalogHotkeyNodeId.nullable().optional(),
    sortOrder: zod_1.z.number().int().min(0).max(10_000).optional(),
    label: zod_1.z.string().min(1).max(120).optional(),
    kind: exports.CatalogHotkeyNodeKind.optional(),
    payload: zod_1.z.string().min(1).max(255).nullable().optional(),
    iconName: zod_1.z.string().min(1).max(64).nullable().optional(),
    // RFL-206: assign (string), clear (null), or leave unchanged (omit)
    // the group node's category binding.
    categoryId: zod_1.z.string().min(1).nullable().optional(),
    expectedVersion: zod_1.z.number().int().min(1),
})
    .superRefine((patch, ctx) => {
    if (patch.kind !== undefined && patch.payload !== undefined) {
        refineKindPayload({ kind: patch.kind, payload: patch.payload }, ctx);
    }
    refineCategoryBinding({ kind: patch.kind, categoryId: patch.categoryId }, ctx);
});
/**
 * PATCH /catalog-hotkeys/reorder body. `orderedChildIds` MUST be the
 * exact current child-set of `parentId` (the server rejects on
 * mismatch). Writes new `sortOrder` values 0..N-1 in one transaction.
 */
exports.CatalogHotkeyReorderRequest = zod_1.z.object({
    parentId: exports.CatalogHotkeyNodeId.nullable(),
    orderedChildIds: zod_1.z.array(exports.CatalogHotkeyNodeId).min(1).max(exports.CATALOG_HOTKEY_TREE_NODE_CAP),
});
// ---------- Function-key vocabulary endpoint ----------
exports.FunctionKeyCategory = zod_1.z.enum(['discount', 'basket', 'system']);
exports.FunctionKeyVocabularyEntry = zod_1.z.object({
    key: exports.FunctionKey,
    label: zod_1.z.string().min(1).max(120),
    category: exports.FunctionKeyCategory,
});
exports.FunctionKeyVocabularyResponse = zod_1.z.object({
    keys: zod_1.z.array(exports.FunctionKeyVocabularyEntry),
    snapshotAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=catalog-hotkeys.js.map